import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { DialogContent, DialogTitle, Grid, Typography, Switch } from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "react-toastify";
import { LocaleContext } from "../../../../routing/LangRouter";
import { convertToJalali, formatGregorianDate } from "../../../../utils/date";
import { navigateToLoginPage, numberWithCommas } from "../../../../utils/utility";
import BASE_URL from "../../../../constants/baseUrls";
import { Add, Eye, Trash } from "iconsax-react";

//general components
import QuestionModal from "../../../common/question-modal/question-modal";
import BaseDialog from "../../../common/base-dialog/base-dialog";
import SmartReservationDetails from "./smart-reservation-details";

export default function SmartReservationCard({ type, fetchData, description, item }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const accessToken = localStorage.getItem("accessToken");

    const [enableToggle, setEnableToggle] = useState(item?.disabled);
    const [isDialogOpen, setIsDialogOpen] = useState({ details: false, delete: false });
    const [selectedItem, setSelectedItem] = useState(null);
    const handleOpenDialog = (type, item) => {
        setIsDialogOpen(value => ({ ...value, [type]: true }));
        setSelectedItem(item);
    };

    const [isLoading, setIsLoading] = useState(false);
    const handleDeleteItem = async () => {
        try {
            setIsLoading(true);
            const response = await axios.delete(`${BASE_URL}/notification/manage/${selectedItem.id}/`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            toast.success(response.data.message);
        } catch (error) {
            if (error.response.status === 401) {
                localStorage.clear();
                navigateToLoginPage(navigate, locale, location);
            } else {
                toast.error(t("Toast Messages.Error Occurred"), { toastId: "custom-id-yes" });
            }
        } finally {
            fetchData();
            setIsDialogOpen({ details: false, delete: false });
            setIsLoading(false);
        }
    };

    const handleActivateItem = async (event, item) => {
        const toggleValue = !event.target.checked;
        setEnableToggle(toggleValue);

        try {
            const response = await axios.delete(`${BASE_URL}/notification/reserve/manage/${item?.id}/`, {
                params: {
                    disabled: toggleValue,
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            toast.success(response.data.message);
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.clear();
                navigateToLoginPage(navigate, locale, location);
            } else {
                toast.error(t("Toast Messages.Error Occurred"), { toastId: "custom-id-yes" });
            }
        } finally {
            fetchData();
        }
    };

    return (
        <>
            <Grid className={`smart-reservation__card ${item?.disabled && "disable"}`} container item xs={12}>
                <Grid className="smart-reservation__info" container item xs={12} justifyContent="space-between" p={2}>
                    <span className="smart-reservation__text">
                        {item?.departure_city?.name ?? item?.departure_airport?.city} -
                        {item?.destination_city?.name ?? item?.destination_airport?.city}
                    </span>
                    <span className="smart-reservation__text">
                        {locale === "fa" ? convertToJalali(item?.departure_date) : formatGregorianDate(item?.departure_date, null, "YYYY/MM/DD")}
                    </span>
                </Grid>
                <Grid container item xs={12} justifyContent="space-between" p={2}>
                    <span className="smart-reservation__text">
                        {numberWithCommas(item?.max_price)} {t("currency")}
                    </span>
                    <Grid container item xs={6} gap={1} justifyContent="flex-end" alignItems="center">
                        <Eye size={20} color="var(--orange-color)" cursor="pointer" onClick={() => handleOpenDialog("details", item)} />
                        {type === "autoBooking" ? (
                            <>
                                <Switch checked={!enableToggle} onChange={event => handleActivateItem(event, item)} color="success" label={"yes"} />
                                <span className="smart-reservation__text">{!enableToggle ? " فعال" : " غیر فعال"} </span>
                            </>
                        ) : (
                            <Trash size={20} color="var(--red-color)" cursor="pointer" onClick={() => handleOpenDialog("delete", item)} />
                        )}
                    </Grid>
                </Grid>
            </Grid>

            <BaseDialog open={isDialogOpen.details} handleClose={() => setIsDialogOpen({ details: false, delete: false })} top="45%" icon="close">
                <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="span" fontSize="1.4rem" fontWeight="bold">
                        {t("details")}
                    </Typography>
                    <Add
                        className="close"
                        size="32"
                        color="var(--teal-color-18)"
                        onClick={() => setIsDialogOpen({ details: false, delete: false })}
                        cursor="pointer"
                    />
                </DialogTitle>
                <DialogContent dividers={true}>
                    <SmartReservationDetails item={selectedItem} type={type} />
                </DialogContent>
            </BaseDialog>

            <QuestionModal
                open={isDialogOpen.delete}
                setOpen={() => {
                    setIsDialogOpen({ details: false, delete: false });
                }}
                question={t("Messages.confirmDelete")}
                description={description}
                colorYes="red"
                colorNo="teal-18"
                handleClick={handleDeleteItem}
                isLoading={isLoading}
            />
        </>
    );
}
